/**
 * AiModeSelector — shared inline dropdown for the agentic-hub AI mode
 * (Manual / Supervised / Autonomous).
 *
 * Extracted from ChatPill so the same pill + ack-modal flow can be reused by
 * EmailRail, SearchAgentPanel, and the AgenticSearchForm advanced-row slot
 * without each surface re-implementing the dropdown, the click-outside
 * close, and the auto/semi-auto acknowledgement modal.
 *
 * Contract:
 *   - `value` is the current mode (controlled). When omitted, defaults to 'semi-auto'.
 *   - `onChange` is called with the new mode AFTER the user confirms any required
 *     acknowledgement (auto / semi-auto fire a one-time confirm dialog gated by
 *     `agentic-hub:ack-auto` / `agentic-hub:ack-fully-auto` localStorage keys).
 *   - `disabled` blocks the dropdown — caller uses this while streaming/sending so
 *     a mid-run mode switch can't fire a 409 server reject.
 *
 * Visual surface = ChatPill's existing pill style (h-7, rounded-full, slate text
 * → black on hover). Pass `className` to override.
 */

import { useEffect, useRef, useState } from 'react'
import { cn } from 'utils/cn'

export type AiModeValue = 'manual' | 'semi-auto' | 'auto'

const AI_MODE_LABELS: Record<AiModeValue, string> = {
    manual: 'Manual',
    'semi-auto': 'Supervised',
    auto: 'Autonomous',
}
const AI_MODES: AiModeValue[] = ['manual', 'semi-auto', 'auto']

// Per-mode acknowledgement keys. Manual is the safest default and never requires
// acknowledgement. Semi-auto and Auto each fire a one-time confirm dialog the
// first time the dispatcher selects them in this browser.
const MODE_ACK_KEYS: Partial<Record<AiModeValue, string>> = {
    'semi-auto': 'agentic-hub:ack-auto',
    auto: 'agentic-hub:ack-fully-auto',
}

export interface AiModeSelectorProps {
    /** Controlled value. When omitted, defaults to 'semi-auto'. */
    value?: AiModeValue
    /** Called with the new mode AFTER any required ack is confirmed. Parent typically
     *  maps this to the backend EmailAgentMode + dispatches toggleMode. */
    onChange?: (mode: AiModeValue) => void
    /** Block the dropdown (streaming / sending — server rejects mid-run mode writes). */
    disabled?: boolean
    /** Visual variant. `pill` (default) matches ChatPill's input bar; `inline` is a
     *  compact bordered chip for a form-section toggle row; `field` is a full-width
     *  h-8 boxed control (value left, chevron right) that sits in a filter cell with
     *  its label on top — used by AgenticSearchForm's pinned wrap column. */
    variant?: 'pill' | 'inline' | 'field'
    /** Where the dropdown popover anchors. `top` (default) opens upward (suits
     *  composer bars at the bottom of the screen); `bottom` opens downward
     *  (suits filter rows where the dropdown shouldn't cover the form above). */
    placement?: 'top' | 'bottom'
    /** Extra classes on the trigger button. */
    className?: string
    /** Title prefix when disabled. Defaults to "Stop the agent to change mode". */
    disabledTitle?: string
    /** Fired when the user opens the mode dropdown (closed → open). Callers use this
     *  for analytics; the ds component stays free of any tracking dependency. */
    onMenuOpen?: () => void
}

export const AiModeSelector = ({
    value,
    onChange,
    disabled = false,
    variant = 'pill',
    placement = 'top',
    className,
    disabledTitle = 'Stop the agent to change mode',
    onMenuOpen,
}: AiModeSelectorProps) => {
    const mode: AiModeValue = value ?? 'semi-auto'
    const [open, setOpen] = useState(false)
    const [pendingMode, setPendingMode] = useState<AiModeValue | null>(null)
    const [dontAskAgain, setDontAskAgain] = useState(false)
    const rootRef = useRef<HTMLDivElement>(null)

    // Close on outside click.
    useEffect(() => {
        if (!open) return
        const onMouseDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
        }
        document.addEventListener('mousedown', onMouseDown)
        return () => document.removeEventListener('mousedown', onMouseDown)
    }, [open])

    const commit = (m: AiModeValue) => {
        setOpen(false)
        onChange?.(m)
    }

    const pickMode = (m: AiModeValue) => {
        const ackKey = MODE_ACK_KEYS[m]
        if (ackKey) {
            try {
                if (!localStorage.getItem(ackKey)) {
                    setDontAskAgain(false)
                    setPendingMode(m)
                    setOpen(false)
                    return
                }
            } catch {
                /* localStorage unavailable — fall through to commit */
            }
        }
        commit(m)
    }

    const confirmPending = () => {
        if (!pendingMode) return
        const ackKey = MODE_ACK_KEYS[pendingMode]
        if (ackKey && dontAskAgain) {
            try {
                localStorage.setItem(ackKey, '1')
            } catch {
                /* ignore */
            }
        }
        commit(pendingMode)
        setPendingMode(null)
        setDontAskAgain(false)
    }

    const cancelPending = () => {
        setPendingMode(null)
        setDontAskAgain(false)
    }

    const triggerClass =
        variant === 'pill'
            ? 'flex items-center gap-0.5 h-7 px-2 rounded-full text-[11px] font-medium text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent'
            : variant === 'field'
              ? // "field" variant — full-width h-8 boxed control matching the filter
                // cells (value left, chevron pushed right via justify-between). The
                // "AI mode" label is owned by the wrapping cell, not the trigger.
                'flex items-center justify-between gap-1 w-full h-8 px-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed'
              : // Compact "inline" variant — small bordered chip suitable for sitting
                // next to a form-section toggle (e.g. AgenticSearchForm's "Advanced
                // filters" row). Same size as DS Button.sm without the surface fill.
                'flex items-center gap-1 h-7 px-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-[11px] font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed'

    const popoverClass = cn(
        'absolute right-0 z-50 w-36 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-xl overflow-hidden',
        placement === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
    )

    return (
        <>
            <div ref={rootRef} className="relative flex-shrink-0">
                <button
                    type="button"
                    onClick={() => {
                        if (disabled) return
                        if (!open) onMenuOpen?.()
                        setOpen((v) => !v)
                    }}
                    disabled={disabled}
                    title={disabled ? disabledTitle : undefined}
                    className={cn(triggerClass, className)}
                >
                    {variant === 'inline' && <span className="text-slate-500 dark:text-slate-400">AI mode:</span>}
                    <span>{AI_MODE_LABELS[mode]}</span>
                    <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="m6 9 6 6 6-6" />
                    </svg>
                </button>
                {open && (
                    <div className={popoverClass}>
                        {AI_MODES.map((m) => (
                            <button
                                key={m}
                                type="button"
                                onClick={() => pickMode(m)}
                                className={cn(
                                    'w-full text-left px-3 py-1.5 text-[12px] transition-colors',
                                    mode === m
                                        ? 'bg-blue-50 dark:bg-blue-500/30 text-blue-700 dark:text-blue-100 font-medium'
                                        : 'text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700'
                                )}
                            >
                                {AI_MODE_LABELS[m]}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Auto / Supervised acknowledgement modal. Fires once per dispatcher per
                mode (gated by localStorage keys) and persists the ack so reload doesn't
                re-prompt. Mirrors main's MessageInput MODE_ACK flow. */}
            {pendingMode && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onMouseDown={cancelPending}>
                    <div
                        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 p-5 max-w-sm w-[min(420px,calc(100vw-2rem))]"
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-[14px] font-semibold text-slate-900 dark:text-slate-100 mb-2">Switch to {AI_MODE_LABELS[pendingMode]} mode?</h3>
                        <p className="text-[12px] text-slate-600 dark:text-slate-400 leading-relaxed">
                            {pendingMode === 'auto'
                                ? 'The agent will send replies to brokers without asking for approval. Make sure you trust the model on this lane before enabling.'
                                : 'The agent will draft replies and act on tools autonomously, but will still ask for approval before sending broker emails.'}
                        </p>
                        <label className="flex items-center gap-2 mt-4 text-[12px] text-slate-600 dark:text-slate-400 cursor-pointer">
                            <input type="checkbox" checked={dontAskAgain} onChange={(e) => setDontAskAgain(e.target.checked)} className="rounded" />
                            <span>Don&rsquo;t ask again for {AI_MODE_LABELS[pendingMode]}</span>
                        </label>
                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                type="button"
                                onClick={cancelPending}
                                className="px-3 py-1.5 text-[12px] font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmPending}
                                className="px-3 py-1.5 text-[12px] font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
                            >
                                Enable {AI_MODE_LABELS[pendingMode]}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
